import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import { PermissionBit, UserRole } from "../../api";
import { ApiConnector } from "../../api/api-connector";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui-kit/base/dialog";
import { Button } from "../../components/ui-kit/button";
import { PermissionsModal } from "../admin/access-policies/permissions-modal";
import { PermissionsClient, ROLE_PRESETS } from "./permissions-client";

export const RoleSwitcher = observer(function RoleSwitcher({
  api,
}: {
  api: ApiConnector;
}) {
  const client = useMemo(() => new PermissionsClient(api), [api]);

  const [selectedRole, setSelectedRole] = useState<UserRole | "">("");
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [password, setPassword] = useState("");

  // Stable reference so PermissionsModal's effect doesn't refetch on every render.
  const loadMetadata = useCallback(
    () => client.loadPermissionsMetadata(),
    [client],
  );

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">Change my permissions</h3>

      <p className="text-xs text-muted-foreground">
        Role presets are hand-maintained to mirror the backend and may be out of
        date.
      </p>

      {client.isAdmin && (
        <p className="text-sm text-muted-foreground">
          Cannot change permissions while logged in as admin.
        </p>
      )}

      <select
        className="rounded-md border border-input bg-background px-3 py-1.5 text-sm [&_option]:bg-popover [&_option]:text-popover-foreground"
        value={selectedRole}
        onChange={(e) => {
          const role = e.target.value as UserRole | "";
          setSelectedRole(role);
          if (role) {
            void client.applyToCurrentUser(ROLE_PRESETS[role]);
          }
        }}
        disabled={client.isWorking || client.isAdmin}
      >
        <option value="">Select role...</option>
        {(Object.keys(ROLE_PRESETS) as UserRole[]).map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </select>

      <Button
        variant="subtle"
        size="sm"
        onClick={() => setPermissionsOpen(true)}
        disabled={client.isWorking || client.isAdmin}
      >
        Edit individual permissions...
      </Button>

      {permissionsOpen && (
        <PermissionsModal
          title="Edit my permissions"
          preselected={api.permissions}
          loadMetadata={loadMetadata}
          onClose={() => setPermissionsOpen(false)}
          onConfirm={({ selected }) =>
            void client.applyToCurrentUser(selected as PermissionBit[])
          }
        />
      )}

      <Dialog
        open={client.needsPassword}
        onOpenChange={(open) => {
          if (!open) {
            client.cancelPasswordPrompt();
            setPassword("");
            // The role/permission change was abandoned; reset the dropdown so it
            // doesn't show a role that was never applied.
            setSelectedRole("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admin authentication</DialogTitle>
          </DialogHeader>
          <input
            type="password"
            placeholder="Admin password"
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && password) {
                void client.submitPassword(password);
              }
            }}
            autoFocus
          />
          {client.passwordError && (
            <p className="text-sm text-danger-foreground">
              {client.passwordError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => void client.submitPassword(password)}
              disabled={!password || client.isWorking}
            >
              {client.isWorking ? "Authenticating..." : "Authenticate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
